import { FC } from "react";
import { useRouter } from "next/router";
import Head from "next/head";
import { getLocalizedPathname } from "modules/localize-pathnames/getLocalizedPathname";

import type { HreflangAlternate } from "components/MetaTags/MetaTags.types";
import { UrlsConfig } from "configs/urlsConfig";
import { unSupportLocaleRoutesLive } from "configs/unsupport-locale-routes";
import { I18nLocaleToUTSLocale, I18nLocale } from "constants/enum";

export interface HreflangServiceProps {
    /** When set (e.g. from CMS page localizations), render only these. Single source for hreflang on that page. */
    overrideAlternates?: HreflangAlternate[] | null;
}

const PAGES_TO_SKIP = [
    UrlsConfig.ERROR,
    `${UrlsConfig.BLOG}/[id]`,
    UrlsConfig.CLINIC,
    `${UrlsConfig.CLINIC}/[id]`,
    UrlsConfig.UNIVERSAL_SELECT,
    UrlsConfig.THANKS_CONTACT,
    UrlsConfig.REFERRAL,
    UrlsConfig.REFERRAL_FOR_FRIENDS,
    UrlsConfig.QUIZ_RESULTS,
    UrlsConfig.VERIFICATION_FAILED,
    UrlsConfig.TYPEFORM_CPH,
    UrlsConfig.BOOKING_SUCCESS,
];

const queryParamsToKeep = (page: UrlsConfig) => {
    switch (page) {
        case UrlsConfig.UNIVERSAL_FORM:
            return ["treatment-type"];
        default:
            return [];
    }
};

export const HreflangService: FC<HreflangServiceProps> = ({
    overrideAlternates,
}) => {
    const { locales, pathname, asPath, query } = useRouter();

    if (overrideAlternates?.length) {
        return (
            <Head key={asPath}>
                {overrideAlternates.map(({ href, hrefLang }) => (
                    <link
                        key={hrefLang}
                        rel="alternate"
                        hrefLang={hrefLang}
                        href={href}
                    />
                ))}
            </Head>
        );
    }

    return (
        <Head key={asPath}>
            {(locales as I18nLocale[]).map((l: I18nLocale) => {
                if ((l as string) === "default") {
                    return;
                }

                if (PAGES_TO_SKIP.includes(pathname as UrlsConfig)) {
                    return;
                }

                const unsupportedLocales = unSupportLocaleRoutesLive[
                    pathname as UrlsConfig
                ] as I18nLocale[];
                if (unsupportedLocales && unsupportedLocales.includes(l)) {
                    return;
                }
                let href;

                if (pathname === UrlsConfig.MAIN) {
                    href = UrlsConfig.HOME_PAGE_URL + `/${l}`;
                } else if (pathname === UrlsConfig.BLOG) {
                    href = UrlsConfig.HOME_PAGE_URL + `/${l}/blog`;
                } else {
                    href =
                        UrlsConfig.HOME_PAGE_URL +
                        `/${l}${getLocalizedPathname(pathname, l, true)}`;
                }

                const queryParamsToKeepList = queryParamsToKeep(
                    pathname as UrlsConfig
                );
                if (queryParamsToKeepList.length > 0) {
                    const filteredQuery = Object.fromEntries(
                        Object.entries(query).filter(([key]) =>
                            queryParamsToKeepList.includes(key)
                        )
                    );
                    href += href.includes("?") ? "&" : "?";
                    href += Object.entries(filteredQuery)
                        .map(([key, value]) => `${key}=${value}`)
                        .join("&");
                }

                return href ? (
                    <link
                        rel="alternate"
                        href={href}
                        hrefLang={I18nLocaleToUTSLocale[l]}
                        key={`alternate-${l}`}
                    />
                ) : null;
            })}
        </Head>
    );
};
